import { ColumnIconType } from "@components/new/Table/useTableSort";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { FC, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { routeNames } from "@core/routeNames";
import Button from "@components/new/Button/Button";
import { components } from "@apiSchema";
import { OfferingChainsData } from "@apiHooks/useOfferingChains";

const MenuItem: FC<components['schemas']['MenuItem']> = (
    {
        title,
        description,
    }
) => {
    return (
        <div className="WhoOffersMenuItem">
            <div
                className="WhoOffersMenuItem__title"
                dangerouslySetInnerHTML={{
                    __html: title ?? '',
                }}
            />
            {description && (
                <div
                    className="WhoOffersMenuItem__description"
                    dangerouslySetInnerHTML={{
                        __html: description
                    }}
                />
            )}
        </div>
    );
};

export function useTableConfig(
    data: OfferingChainsData['menuItemsData'],
    sortingField: keyof OfferingChainsData['menuItemsData'][number] | null,
    sortingDirection: 'asc' | 'desc',
    handleSort: (fieldName: keyof OfferingChainsData['menuItemsData'][number]) => () => void,
    getColumnIcon: ColumnIconType,
    expandedRowIndexes: number[],
    setExpandedRowIndexes: (newValue: number[]) => void,
): iTableConfigItem[] {
    const columnIcon = useCallback(
        (fieldName: keyof OfferingChainsData['menuItemsData'][number]) => getColumnIcon(fieldName, sortingField, sortingDirection),
        [getColumnIcon, sortingDirection, sortingField]
    );

    useEffect(() => {
        setExpandedRowIndexes([]);
    }, [sortingField, sortingDirection, setExpandedRowIndexes]);

    return useMemo((): iTableConfigItem[] => [
        {
            width: 300,
            title: 'Chain',
            icon: columnIcon('chainName'),
            isActive: sortingField === 'chainName',
            styles: {
                cell: {
                    position: 'sticky',
                    top: 0,
                    bottom: 0,
                    minHeight: 0,
                }
            },
            Value: ({ index }) => (
                <Link
                    className="NewTopChains__link"
                    to={{
                        pathname: routeNames.chainDetails,
                        search: `id=${data[index].chainId}`,
                    }}
                >
                    {data[index].chainName}
                </Link>
            ),
            handleHeaderClick: handleSort('chainName'),
        },
        {
            title: 'Menu items',
            styles: { cell: { height: 'auto', minHeight: 40 } },
            Value: ({ index }) => (
                <div className="WhoOffers__menu-items">
                    {!expandedRowIndexes.includes(index) && (
                        <MenuItem
                            {...data[index].menuItems?.[0]}
                        />
                    )}
                    {expandedRowIndexes.includes(index) && data[index].menuItems?.map((i, iIndex) => (
                        <MenuItem
                            key={`${data[index].chainId}-${iIndex}`}
                            {...i}
                        />
                    ))}
                </div>
            )
        },
        {
            width: 130,
            title: '',
            Value: ({ index }) => (
                <>
                    {(data[index].menuItems?.length ?? 0) > 1 && (
                        <>
                            {expandedRowIndexes.includes(index)
                                ? (
                                    <Button
                                        modifiers={["secondary"]}
                                        onClick={() => setExpandedRowIndexes([...expandedRowIndexes.filter(i => i !== index)])}
                                        testId={`buttonFoodsViewLess-${index}`}
                                    >
                                        View less
                                    </Button>
                                )
                                : (
                                    <Button
                                        modifiers={["secondary"]}
                                        onClick={() => setExpandedRowIndexes([...expandedRowIndexes, index])}
                                        testId={`buttonFoodsViewMore-${index}`}
                                    >
                                        plus {(data[index].menuItems?.length ?? 0) - 1} more
                                    </Button>
                                )}
                        </>
                    )}
                </>
            )
        }
    ], [columnIcon, handleSort, data, sortingField, expandedRowIndexes, setExpandedRowIndexes])
}